var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { OnInit, EventEmitter, ElementRef, OnDestroy } from '@angular/core';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { PublishService } from '../../publish.service';
import { CameraService } from 'src/app/engine/camera.service';
import { Observable, Subject, interval } from 'rxjs';
import { map, takeUntil, filter, tap } from 'rxjs/operators';
import { WidgetOptions } from '../../models/widget-options.model';
import { animationFrame } from 'rxjs/internal/scheduler/animationFrame';
import { BackgroundModel } from 'src/app/engine/models/background.model';
var scaleSmoothFactor = 0.95;
var HumanAPIEvent;
(function (HumanAPIEvent) {
    HumanAPIEvent["HumanReady"] = "human.ready";
    HumanAPIEvent["CameraUpdated"] = "camera.updated";
})(HumanAPIEvent || (HumanAPIEvent = {}));
var HumanAPIMessage;
(function (HumanAPIMessage) {
    HumanAPIMessage["GetCameraInfo"] = "camera.info";
    HumanAPIMessage["SetCameraOrbit"] = "camera.orbit";
    HumanAPIMessage["SetBackgroundColor"] = "ui.setBackground";
    HumanAPIMessage["GetBackgroundColor"] = "ui.getBackground";
    HumanAPIMessage["SetWidgetConfig"] = "ui.setDisplay";
    HumanAPIMessage["SetInitialEnabled"] = "widget.enableInitial";
    HumanAPIMessage["SetInitialDisabled"] = "widget.disableInitial";
    HumanAPIMessage["SetScrollEnabled"] = "widget.enableScroll";
    HumanAPIMessage["SetScrollDisabled"] = "widget.disableScroll";
    HumanAPIMessage["SetToolsDisplay"] = "widget.toolsDisplay";
    HumanAPIMessage["GetSnapshot"] = "ui.snapshot";
})(HumanAPIMessage || (HumanAPIMessage = {}));
var PublishWidgetComponent = /** @class */ (function () {
    function PublishWidgetComponent(elRef, publishService, cameraService) {
        this.elRef = elRef;
        this.publishService = publishService;
        this.cameraService = cameraService;
        this.scalable = false;
        this.cameraUpdated = new EventEmitter();
        this.ngUnsubscribe = new Subject();
        this._ready = false;
    }
    Object.defineProperty(PublishWidgetComponent.prototype, "bgColor", {
        set: function (value) {
            this._bgColor = value;
            if (this._ready) {
                this.renderWidgetBGColor();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "widgetParams", {
        set: function (value) {
            this._params = value;
            this._rotateDegrees = value ? value.loadRotate : 0;
            this.setWidgetUIConfig();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "topOffset", {
        get: function () {
            var containerHeight = this.elRef.nativeElement.offsetHeight;
            var targetOffset = (containerHeight - this.height) / 2;
            return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "leftOffset", {
        get: function () {
            var containerWidth = this.elRef.nativeElement.offsetWidth;
            var targetOffset = (containerWidth - this.width) / 2;
            return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "scaleFactor", {
        get: function () {
            if (!this.scalable) {
                return 1;
            }
            else {
                var _a = this.containerRef.nativeElement, offsetWidth = _a.offsetWidth, offsetHeight = _a.offsetHeight;
                var xScale = offsetWidth / this.width;
                var yScale = offsetHeight / this.height;
                var targetScale = Math.min(xScale, yScale, 1);
                if (targetScale === 1) { // default scale
                    return 1;
                }
                else { // scaled model: smooth
                    return targetScale * scaleSmoothFactor;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    PublishWidgetComponent.prototype.ngOnInit = function () {
        var _this = this;
        var frameId = this.frameRef.nativeElement.id;
        this._apiRef = new HumanAPI({ iframeId: frameId });
        // listen for API ready state
        this.getAPIEvent(HumanAPIEvent.HumanReady)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function () {
            // ready state
            _this._ready = true;
            _this.setupCameraTracking();
            _this.setupCameraRotation();
            _this.setWidgetUIConfig();
            // setup background color
            _this.getCurrentBGColor()
                .pipe(takeUntil(_this.ngUnsubscribe))
                .subscribe(function (color) {
                _this._defaultColor = color;
                // initial bg color
                _this.renderWidgetBGColor();
            });
        });
    };
    PublishWidgetComponent.prototype.ngOnDestroy = function () {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    };
    PublishWidgetComponent.prototype.setWidgetUIConfig = function () {
        // API ready
        if (this._apiRef) {
            if (this._params) { // set UI
                var uiConfig = this.publishService.getWidgetConfig(this._params);
                var _a = this._params.initial, button = _a.button, handHint = _a.handHint;
                var initialMode = button ? 'default' : (handHint ? 'hand-hint' : '');
                var _b = this._params.disableScroll, alert_1 = _b.alert, overlay = _b.overlay;
                var scrollMode = alert_1 ? 'alert' : (overlay ? 'overlay' : '');
                var uiToolsDisplay = this._params.uiToolsDisplay;
                this.setUIOptions(uiConfig);
                this.setInitialMode(initialMode);
                this.setScrollMode(scrollMode);
                this.setToolsDisplay(uiToolsDisplay);
            }
            else { // disable UI
                this.setUIOptions({ all: false });
                this.setInitialMode();
                this.setScrollMode();
                this.setToolsDisplay();
            }
        }
    };
    PublishWidgetComponent.prototype.takeSnapshot = function (dimensions) {
        return this.getAPIData(HumanAPIMessage.GetSnapshot, __assign({ width: this.width, height: this.height }, dimensions));
    };
    /**
     * Orbits the camera around the model by specified degrees.
     */
    PublishWidgetComponent.prototype.setCameraOrbit = function (degrees) {
        this.sendAPIData(HumanAPIMessage.SetCameraOrbit, {
            yaw: degrees
        });
    };
    /**
   * Sets UI options (i.e., zoom, fullscreen, help, etc...)
   */
    PublishWidgetComponent.prototype.setUIOptions = function (uiConfig) {
        this.sendAPIData(HumanAPIMessage.SetWidgetConfig, uiConfig);
    };
    /**
     * Sets the initial mode (i.e., click to interact)
     */
    PublishWidgetComponent.prototype.setInitialMode = function (initialMode) {
        if (initialMode === void 0) { initialMode = ''; }
        if (initialMode) {
            this.sendAPIData(HumanAPIMessage.SetInitialEnabled, initialMode);
        }
        else { // disable initial
            this.sendAPIData(HumanAPIMessage.SetInitialDisabled);
        }
    };
    /**
     * Sets the scroll override mode (for mouse wheel)
     */
    PublishWidgetComponent.prototype.setScrollMode = function (scrollMode) {
        if (scrollMode === void 0) { scrollMode = ''; }
        if (scrollMode) {
            this.sendAPIData(HumanAPIMessage.SetScrollDisabled, scrollMode);
        }
        else { // disable initial
            this.sendAPIData(HumanAPIMessage.SetScrollEnabled);
        }
    };
    /**
     * Sets the toolbar grouping
     */
    PublishWidgetComponent.prototype.setToolsDisplay = function (uiToolsDisplay) {
        if (uiToolsDisplay === void 0) { uiToolsDisplay = ''; }
        this.sendAPIData(HumanAPIMessage.SetToolsDisplay, uiToolsDisplay);
    };
    PublishWidgetComponent.prototype.renderWidgetBGColor = function () {
        this.setWidgetBGColor(this._bgColor || this._defaultColor);
    };
    PublishWidgetComponent.prototype.setWidgetBGColor = function (background) {
        if (this._apiRef && background) {
            this.sendAPIData(HumanAPIMessage.SetBackgroundColor, background);
        }
    };
    PublishWidgetComponent.prototype.setupCameraTracking = function () {
        var _this = this;
        // get initial camera
        this.getCurrentCamera()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function (currentCamera) {
            // set fixed default camera
            _this.setDefaultCamera(currentCamera);
            // begin tracking camera changes
            _this.getCameraUpdates()
                .pipe(takeUntil(_this.ngUnsubscribe))
                .subscribe(function (updateCamera) {
                _this.setUpdateCamera(updateCamera);
            });
        });
    };
    // use API to rotate widget fixed amount in an interval
    PublishWidgetComponent.prototype.setupCameraRotation = function () {
        var _this = this;
        // setup camera rotate animation
        var timeLast = performance.now();
        interval(0, animationFrame)
            .pipe(filter(function () { return !!_this._rotateDegrees; }), // on valid rotation values
        map(function () { return _this._rotateDegrees * (performance.now() - timeLast) * 0.001; }), tap(function () { return timeLast = performance.now(); }), takeUntil(this.ngUnsubscribe))
            .subscribe(function (degrees) {
            _this.setCameraOrbit(degrees);
        });
    };
    PublishWidgetComponent.prototype.setDefaultCamera = function (camera) {
        this._defaultCamera = camera;
        this.setUpdateCamera(camera);
    };
    PublishWidgetComponent.prototype.setUpdateCamera = function (camera) {
        this._currentCamera = camera;
        // #TODO: compare cameras, smooth ???
        var camerasMatch = this.cameraService.compareCameras(this.cameraService.roundCamera(this._defaultCamera, 3), this.cameraService.roundCamera(this._currentCamera, 3));
        // dispatch changes: validate delta from original
        var targetCamera = !camerasMatch ? this._currentCamera : null;
        this.cameraUpdated.emit(targetCamera);
    };
    PublishWidgetComponent.prototype.getAPIEvent = function (event) {
        var _this = this;
        return new Observable(function (observer) {
            _this._apiRef.on(event, function (data) {
                observer.next(data);
            });
        });
    };
    PublishWidgetComponent.prototype.getAPIData = function (message, payload) {
        var _this = this;
        return new Observable(function (observer) {
            _this._apiRef.send(message, payload, function (data) {
                observer.next(data);
                observer.complete();
            });
        });
    };
    PublishWidgetComponent.prototype.sendAPIData = function (message, payload) {
        this._apiRef.send(message, payload);
    };
    PublishWidgetComponent.prototype.getCurrentCamera = function () {
        return this.getAPIData(HumanAPIMessage.GetCameraInfo)
            .pipe(map(function (data) {
            return {
                eye: data.position,
                look: data.target,
                up: data.up
            };
        }));
    };
    PublishWidgetComponent.prototype.getCurrentBGColor = function () {
        return this.getAPIData(HumanAPIMessage.GetBackgroundColor)
            .pipe(map(function (data) {
            if (data) {
                return new BackgroundModel(__spread([].concat.apply([], __spread(data.colors))), // [[r,g,b,a], [r,g,b,a]] -> [r,g,b,a,r,g,b,a]
                data.gradientType, data.colorStops);
            }
            else {
                return null;
            }
        }));
    };
    PublishWidgetComponent.prototype.getCameraUpdates = function () {
        return this.getAPIEvent(HumanAPIEvent.CameraUpdated)
            .pipe(map(function (data) {
            var position = data.position, target = data.target, up = data.up;
            return {
                eye: position,
                look: target,
                up: up
            };
        }));
    };
    return PublishWidgetComponent;
}());
export { PublishWidgetComponent };
